import React, {useState, useEffect, useLayoutEffect, useRef, useCallback, useContext } from "react";
import SearchBar from './SearchBar.jsx';
import Map from './Map.jsx';
import ResultsList from './ResultsList.jsx';
import OpenResults from './OpenResults.jsx';
import { DealerContext } from '../../contexts/DealerContext.jsx';
import { useSpring, animated } from '@react-spring/web'
import { useDrag } from '@use-gesture/react'

const ResultsContainer = ({}) => {

  const { mobileDefaultView, loading } = useContext(DealerContext);
  // mobile defaults to the map unless the "Default to results list on
  // mobile" section setting says otherwise (see main-dealer-locator.liquid).
  const mobileOpensMap = window.innerWidth < 1000 && mobileDefaultView !== 'results';

  const resultsContainer = useRef(null);
  const searchBarRef = useRef(null);
  const utilityBarRef = useRef(null);
  const [mapOpen, setMapOpen] = useState(mobileOpensMap);
  const [mapVisible, setMapVisible] = useState(mobileOpensMap);
  const [{ width, height }, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  })
  const bottomOffset = useRef(0);
  const isMapClosed = useRef(true); // this feels unnecessary but I'm not sure how else to cancel the mapVisible set state in the slideResultsUp callback

  const [{ y }, api] = useSpring(() => ({ y: 0 }))

  const [{scroll}, scrollApi] = useSpring(() => ({
    immediate: false,
    scroll: window.scrollY,
    onChange: props => {
      window.scroll(0, props.value.scroll);
    },
  }));

  // scroll position that puts the search bar right under the sticky header, so the fixed map fills the rest of the screen
  const getMapScrollTarget = () => {
    if(!searchBarRef.current) return 0;
    const styles = getComputedStyle(document.documentElement);
    // scroll target = search bar's document offset minus the header's own height (the card, not the section's transparent space)
    const headerCard = document.querySelector('.shopify-section--header .header__wrapper__inner');
    const headerHeightVar = parseFloat(styles.getPropertyValue('--header-height')) || 0;
    const stickyTop = headerCard ? headerCard.offsetHeight : headerHeightVar;
    const searchBarTop = window.scrollY + searchBarRef.current.getBoundingClientRect().top;
    const target = Math.max(0, searchBarTop - stickyTop);
    return target;
  };

  useLayoutEffect(() => {
    const resize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  // only scroll to the map position when the map opens. This used to live in the effect below, which also re-runs on every width/height change,
  // and iOS Safari fires resize as the URL bar collapses while scrolling, so the page got pulled back to the map position mid-scroll.
  // The first mount can happen late (the IP block list fetch in dealer-locator.js delays render), after the visitor has already started
  // scrolling the server-rendered skeleton, so don't yank them back to the map position on mount if they've scrolled.
  const mountedRef = useRef(false);
  useEffect(() => {
    const firstRun = !mountedRef.current;
    mountedRef.current = true;
    if(mapOpen && firstRun && window.scrollY > 0) return;
    if(mapOpen) {
      scrollApi.set({scroll: window.scrollY})
      scrollApi.start({scroll: getMapScrollTarget()});
    }
  }, [mapOpen])

  useEffect(() => {
    if(mapOpen) {
      isMapClosed.current = false;
      slideResultsDown();
    } else {
      isMapClosed.current = true;
      slideResultsUp();
    }
  }, [mapOpen, width, height])

  // map view runs under the header, so show the header in its scrolled overlay state even when the page hasn't scrolled.
  // is-sticky is what makes the header section sticky (otherwise it's position: relative and scrolls away), is-filled gives it the background + shadow.
  useEffect(() => {
    const header = document.querySelector('store-header');
    if(!header || !mapOpen) return;
    const hadSticky = header.classList.contains('is-sticky');
    header.classList.add('is-sticky', 'is-filled');
    // the header's own scroll/hover logic strips these, so keep them on while the map is open
    const observer = new MutationObserver(() => {
      if(!header.classList.contains('is-filled') || !header.classList.contains('is-sticky')){
        header.classList.add('is-sticky', 'is-filled');
      }
    });
    observer.observe(header, { attributes: true, attributeFilter: ['class'] });
    return () => {
      observer.disconnect();
      if(!hadSticky) header.classList.remove('is-sticky');
      if(window.scrollY <= 200) header.classList.remove('is-filled');
    };
  }, [mapOpen]);

  // the skeleton has no utilityBarRef, so the offset measured on first load is too tall. Re-measure once the real results render.
  useEffect(() => {
    if(mapOpen && !loading) slideResultsDown();
  }, [loading]);

  const slideResultsDown = useCallback(() => {
    setMapVisible(true);
    document.documentElement.classList.add("lock");

    const utilityBarHeight = utilityBarRef.current ? utilityBarRef.current.getBoundingClientRect().height : 35; // 35 = loaded bar height, used while the skeleton has no ref
    // search bar bottom as it will sit once scrolled to the map position
    const utilityBarOffset = searchBarRef.current ? window.scrollY + searchBarRef.current.getBoundingClientRect().bottom - getMapScrollTarget() : 0;
    const windowHeight = window.innerHeight;
    bottomOffset.current = windowHeight - utilityBarOffset - utilityBarHeight - 50;

    api.start({ y: bottomOffset.current })
  }, [])

  const slideResultsUp = useCallback(async () => {
    document.documentElement.classList.remove("lock");
    await api.start({ y: 0 })[0];
    if(isMapClosed.current){
      setMapVisible(false);
    }
  }, [])

  const handleHandleClick = () => {
    setMapOpen(false);
  }

  const bind = useDrag(({ down, movement: [mx, my], velocity: [vx, vy] }) => {
    if(!mapOpen) return;
    if(down){
      api.start({ y: my + bottomOffset.current })
    } else if (my * -2 > bottomOffset.current || vy > 1){
      setMapOpen(false);
    } else {
      api.start({ y: bottomOffset.current })
    }
  })

  const bindProps = mapVisible ? {
    ...bind()
  } : {}

  const mobile = width < 1000;

  return (
    <div className={`ft_dealer-locator__results-container ${mapOpen || !mobile ? 'map-open' : ''}`}>
      <div className="ft_dealer-locator__results-lower-container ft_dealer-locator__container">
        {mobile ? <SearchBar ref={searchBarRef}/> : null}
        {mapVisible || !mobile ? <Map /> : null}
        <animated.div style={{ y, touchAction: mapVisible ? 'none' : "auto" }} {...bindProps} className="results_sidebar" ref={resultsContainer}>
          {!mobile ? <SearchBar ref={searchBarRef}/> : null}
          <button className="button--handle lg:hidden" onClick={handleHandleClick}>Close Map</button>
          <ResultsList openMap={() => setMapOpen(!mapOpen)} mapOpen={mapOpen} utilityBarRef={utilityBarRef} />
        </animated.div>
        {mobile && <OpenResults bottomOffset={bottomOffset} />}
      </div>
    </div>
  );

}

export default ResultsContainer;
